import { inject } from '@angular/core';
import { Route, Router } from '@angular/router';
import { featureFlagGuard } from 'app/core/guards/feature-flag.guard';
import { AuthService } from 'app/core/services/auth.service';
import { FEATURE_FLAGS } from 'app/core/services/feature-flag.service';
import { canUseSupportTickets, ROLE_GROUPS } from 'app/shared/constants';

/** Additional PhotoFlow product routes (non-IAM). */
export const adminFeatureMoreRoutes: Route[] = [
    // SA commerce manage (quota/license/admin-subs) → nera-sa-portal only
    {
        path: 'my-notifications',
        loadChildren: () =>
            import(
                'app/features/admin/my-notifications/my-notifications.routes'
            ),
    },
    // Scheduler (cron jobs, triggers, job dependencies) → nera-sa-portal only
    // /dam and /storage sit behind Unleash pfl-dam (mvp-release-scope §5.2).
    // NOT the Drive OAuth return (storage/connect/callback, below) nor
    // /me/storage-connection: album Drive import sends users through both
    // when it hits STORAGE_CONNECTION_MISSING, and Album is in the release.
    {
        path: 'dam',
        loadChildren: () => import('app/features/admin/dam/dam.routes'),
        canActivate: [featureFlagGuard(FEATURE_FLAGS.DAM)],
        data: { anyRoles: [...ROLE_GROUPS.DAM_DOCUMENT_READERS] },
    },
    // MUST stay ahead of the 'storage' route below. 'storage' is a prefix match with
    // loadChildren, and once the router commits to a lazy route it does not backtrack to a
    // sibling — 'storage/connect/callback' would be swallowed by 'storage', whose children
    // only declare path '', leaving the OAuth return trip on a blank, guard-less page.
    {
        path: 'storage/connect/callback',
        loadChildren: () =>
            import(
                'app/features/admin/storage/pages/connect-callback/storage-connect-callback.routes'
            ),
    },
    {
        path: 'storage',
        loadChildren: () => import('app/features/admin/storage/storage.routes'),
        canActivate: [featureFlagGuard(FEATURE_FLAGS.DAM)],
        data: { anyRoles: [...ROLE_GROUPS.PHOTOGRAPHER_AND_ABOVE] },
    },
    // PhotoQA (T5.8): moved to its own single top-level `/photoqa` mount in
    // `app.routes.ts` — see `features/photoqa/photoqa.routes.ts`.
    {
        path: 'content-library',
        loadChildren: () =>
            import('app/features/admin/content-library/content-library.routes'),
    },
    // Customer helpdesk tickets (D32(a), role-billing-redesign 2026-09-23 plan §6.12/§8.1).
    // Same roles as helpdesk-service's HelpDesk.Read policy.
    {
        path: 'support',
        loadChildren: () => import('app/features/admin/support/support.routes'),
        canActivate: [
            () =>
                canUseSupportTickets(inject(AuthService).user?.roles) ||
                inject(Router).createUrlTree(['/forbidden']),
        ],
    },
    // Marketplace MVP (D39) — contract §9. The mount itself is open to any
    // authenticated user (ANY_AUTHENTICATED); finer-grained gating (studio
    // owner tier vs personal SystemOrg profile vs any signed-in freelancer)
    // is enforced per child route inside marketplace.routes.ts.
    {
        path: 'marketplace',
        loadChildren: () =>
            import('app/features/admin/marketplace/marketplace.routes'),
        data: { anyRoles: [...ROLE_GROUPS.ANY_AUTHENTICATED] },
    },
    // Platform Audit → nera-sa-portal only (see /event-log there; T2.1/T2.2).
];
